/**
 * Energy Role - Validate Pillar
 *
 * Validação semântica de valores de energia.
 * Zero dependências externas - pode ser usado standalone.
 *
 * @example
 * import { validateEnergy, isValidEnergy } from '@attrx/role-morphic/energy/validate';
 *
 * validateEnergy(100);   // { valid: true, errors: [] }
 * validateEnergy(-5);    // { valid: false, errors: ['Energy cannot be negative'] }
 * isValidEnergy(100);    // true
 */

// =============================================================================
// TYPES
// =============================================================================

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

export interface EnergyValidationConfig {
  min?: number;
  max?: number;
  integer?: boolean;
  minError?: string;
  maxError?: string;
}

// =============================================================================
// CONFIG
// =============================================================================

/**
 * Configuração padrão de validação para energia
 */
export const ENERGY_VALIDATION_CONFIG: EnergyValidationConfig = {
  min: 0,
  minError: "Energy cannot be negative",
};

// =============================================================================
// VALIDATE
// =============================================================================

/**
 * Valida um valor de energia
 *
 * @param value - Valor a ser validado
 * @param config - Configuração customizada (opcional)
 * @returns Resultado da validação com erros se houver
 *
 * @example
 * validateEnergy(100);              // { valid: true, errors: [] }
 * validateEnergy(-5);               // { valid: false, errors: ['Energy cannot be negative'] }
 * validateEnergy(150, { max: 100 }) // { valid: false, errors: ['Value must be at most 100'] }
 */
export function validateEnergy(
  value: unknown,
  config: EnergyValidationConfig = ENERGY_VALIDATION_CONFIG
): ValidationResult {
  const errors: string[] = [];

  // Type check
  if (typeof value !== "number") {
    return { valid: false, errors: ["Value must be a number"] };
  }

  // Finite check
  if (!Number.isFinite(value)) {
    return { valid: false, errors: ["Value must be finite"] };
  }

  // Min check
  if (config.min !== undefined && value < config.min) {
    const msg = config.minError ?? `Value must be at least ${config.min}`;
    errors.push(msg);
  }

  // Max check
  if (config.max !== undefined && value > config.max) {
    const msg = config.maxError ?? `Value must be at most ${config.max}`;
    errors.push(msg);
  }

  // Integer check
  if (config.integer && !Number.isInteger(value)) {
    errors.push("Value must be an integer");
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Verifica se um valor de energia é válido
 *
 * @param value - Valor a ser validado
 * @param config - Configuração customizada (opcional)
 * @returns true se válido, false caso contrário
 *
 * @example
 * isValidEnergy(100);  // true
 * isValidEnergy(-5);   // false
 */
export function isValidEnergy(
  value: unknown,
  config: EnergyValidationConfig = ENERGY_VALIDATION_CONFIG
): boolean {
  return validateEnergy(value, config).valid;
}
